import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { BookOpen, Loader2, Check, X, Send, Wand2, ChevronDown, ChevronRight, Edit2, ExternalLink, Save, Plus, Trash2, EyeOff, RotateCcw, Square, CheckSquare, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Default account suggestions
const ACCOUNT_SUGGESTIONS = [
  { code: '1200', name: 'Crypto Assets' },
  { code: '4000', name: 'Crypto Income' },
  { code: '5000', name: 'Crypto Payments' },
  { code: '5100', name: 'Transaction Fees' },
  { code: '6000', name: 'Operating Expenses' },
  { code: '7200', name: 'Realized Gains/Losses' },
];

// Account classes for grouping
const ACCOUNT_CLASSES = [
  { id: 'ASSET', label: 'Assets', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  { id: 'LIABILITY', label: 'Liabilities', color: 'text-red-400', bgColor: 'bg-red-500/10' },
  { id: 'EQUITY', label: 'Equity', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  { id: 'REVENUE', label: 'Revenue', color: 'text-green-400', bgColor: 'bg-green-500/10' },
  { id: 'EXPENSE', label: 'Expenses', color: 'text-orange-400', bgColor: 'bg-orange-500/10' },
];

// Compact Account Dropdown Component for Journal Entry Editor
function AccountSelectDropdown({ value, onChange, accounts = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClasses, setExpandedClasses] = useState(['EXPENSE', 'ASSET']);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleClass = (cls) => {
    setExpandedClasses(prev => 
      prev.includes(cls) 
        ? prev.filter(c => c !== cls)
        : [...prev, cls]
    );
  };

  const getSelectedAccount = () => {
    if (!value) return null;
    return accounts.find(a => a.code === value);
  };

  const selectedAccount = getSelectedAccount();

  // Filter and group accounts
  const filteredAccounts = accounts.filter(acc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (acc.code || '').toLowerCase().includes(query) ||
           (acc.name || '').toLowerCase().includes(query);
  });

  const groupedAccounts = ACCOUNT_CLASSES.map(cls => ({
    ...cls,
    accounts: filteredAccounts.filter(acc => acc.class === cls.id)
  })).filter(group => group.accounts.length > 0);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-1 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-left hover:border-zinc-600 transition-colors h-8"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {selectedAccount ? (
            <>
              <span className="font-mono text-zinc-400 text-xs">#{selectedAccount.code}</span>
              <span className="text-white text-sm truncate max-w-[100px]">{selectedAccount.name}</span>
            </>
          ) : (
            <span className="text-zinc-400 text-sm">Select...</span>
          )}
        </div>
        <ChevronDown className={`w-3 h-3 text-zinc-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-72 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-72 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-zinc-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search accounts..."
                className="w-full pl-8 pr-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm focus:outline-none focus:border-zinc-600"
                autoFocus
              />
            </div>
          </div>

          {/* Account List */}
          <div className="overflow-y-auto max-h-56">
            {groupedAccounts.length === 0 ? (
              <div className="px-3 py-4 text-center text-zinc-500 text-sm">
                No accounts found
              </div>
            ) : (
              groupedAccounts.map(group => {
                const isExpanded = expandedClasses.includes(group.id);
                
                return (
                  <div key={group.id} className="border-b border-zinc-800/50 last:border-0">
                    {/* Class Header */}
                    <div
                      onClick={() => toggleClass(group.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-800/50 ${group.bgColor}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className={`w-3 h-3 ${group.color}`} />
                      ) : (
                        <ChevronRight className={`w-3 h-3 ${group.color}`} />
                      )}
                      <span className={`font-medium text-sm ${group.color}`}>{group.label}</span>
                      <span className="text-zinc-500 text-xs ml-auto">({group.accounts.length})</span>
                    </div>

                    {/* Accounts */}
                    {isExpanded && (
                      <div className="pb-1">
                        {group.accounts.map(acc => (
                          <div
                            key={acc.code}
                            onClick={() => {
                              onChange(acc.code, acc.name);
                              setIsOpen(false);
                              setSearchQuery('');
                            }}
                            className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-700/50 mx-1 rounded ${
                              value === acc.code ? 'bg-blue-600/20' : ''
                            }`}
                          >
                            <span className="font-mono text-zinc-400 text-xs w-10">{acc.code}</span>
                            <span className="text-white text-sm truncate">{acc.name}</span>
                            {acc.xero_account_id && (
                              <span className="ml-auto px-1 py-0.5 text-[9px] rounded bg-[#13B5EA]/20 text-[#13B5EA]">
                                Xero
                              </span>
                            )}
                            {value === acc.code && (
                              <Check className="w-3 h-3 text-blue-400 ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CointrackerJournalEntries() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [editedLines, setEditedLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [xeroConnected, setXeroConnected] = useState(false);

  useEffect(() => {
    fetchEntries();
    fetchAccounts();
    checkXeroStatus();
  }, [pagination.page, statusFilter]);

  const checkXeroStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/xero/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setXeroConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Xero status:', error);
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/cointracker/journal-entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEntries(response.data.entries || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const importedAccounts = response.data.accounts || [];
      
      if (importedAccounts.length > 0) {
        // Use only Xero/imported accounts - sorted by code
        setAccounts(importedAccounts.sort((a, b) => 
          (a.code || '').localeCompare(b.code || '')
        ));
      } else {
        // Fallback to defaults only if no accounts imported
        setAccounts(ACCOUNT_SUGGESTIONS.map(acc => ({
          ...acc,
          class: acc.code.startsWith('1') ? 'ASSET' :
                 acc.code.startsWith('4') ? 'REVENUE' :
                 acc.code.startsWith('5') || acc.code.startsWith('6') ? 'EXPENSE' :
                 acc.code.startsWith('7') ? 'EQUITY' : 'EXPENSE'
        })));
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setAccounts(ACCOUNT_SUGGESTIONS.map(acc => ({
        ...acc,
        class: acc.code.startsWith('1') ? 'ASSET' :
               acc.code.startsWith('4') ? 'REVENUE' :
               acc.code.startsWith('5') || acc.code.startsWith('6') ? 'EXPENSE' :
               acc.code.startsWith('7') ? 'EQUITY' : 'EXPENSE'
      })));
    }
  };
  
  // Helper to get account name from code
  const getAccountName = (code) => {
    if (!code) return '';
    const account = accounts.find(a => a.code === code);
    return account?.name || `Account ${code}`;
  };

  const handleGenerateEntries = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/journal-entries/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Generated ${response.data.generated} journal entries`);
      fetchEntries();
    } catch (error) {
      console.error('Failed to generate entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate entries');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateAll = async () => {
    if (!confirm('⚠️ This will DELETE all journal entries and regenerate them from transactions. Are you sure?')) {
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/journal-entries/regenerate-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Regenerated ${response.data.generated} entries (${response.data.rules_applied} rules applied)`);
      fetchEntries();
    } catch (error) {
      console.error('Failed to regenerate entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate entries');
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/journal-entries/${entryId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry approved');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to approve entry');
    }
  };

  const handleIgnoreEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/journal-entries/${entryId}/ignore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry ignored');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to ignore entry');
    }
  };

  const handleRegenerateEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/journal-entries/${entryId}/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry regenerated');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to regenerate entry');
    }
  };

  const handleRejectEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/journal-entries/${entryId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry rejected');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to reject entry');
    }
  };

  const handleSyncToXero = async () => {
    if (!xeroConnected) {
      toast.error('Please connect Xero first in Settings');
      return;
    }
    
    const entriesToSync = selectedEntries.size > 0 
      ? entries.filter(e => selectedEntries.has(e.entry_id) && e.status === 'approved' && !e.xero_journal_id)
      : entries.filter(e => e.status === 'approved' && !e.xero_journal_id);
    
    if (entriesToSync.length === 0) {
      toast.error('No approved entries to sync. Select entries or approve some first.');
      return;
    }
    
    setSyncing(true);
    const token = localStorage.getItem('token');
    let synced = 0;
    const failures = [];
    
    try {
      for (const entry of entriesToSync) {
        try {
          await axios.post(`${API_URL}/cointracker/xero/sync-journal-entry/${entry.entry_id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          synced++;
        } catch (error) {
          // Surface what Xero actually said: "N entries failed" gives nothing to act on
          const detail = error.response?.data?.detail || error.message || 'Unknown error';
          console.error(`Failed to sync entry ${entry.entry_id}:`, detail);
          failures.push({ entry, detail: String(detail) });
        }
      }
      
      if (synced > 0) {
        toast.success(`${synced} entries synced to Xero as DRAFT`);
      }
      if (failures.length > 0) {
        const { entry, detail } = failures[0];
        const reference = entry.reference || entry.entry_id.slice(0, 6);
        const others = failures.length > 1 ? ` (+${failures.length - 1} more)` : '';
        toast.error(`Xero refused ${reference}${others}: ${detail.slice(0, 300)}`, {
          duration: 15000,
        });
      }
      
      setSelectedEntries(new Set());
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync to Xero');
    } finally {
      setSyncing(false);
    }
  };

  const toggleSelectEntry = (entryId) => {
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const syncableEntries = entries.filter(e => e.status === 'approved' && !e.xero_journal_id);
    if (selectedEntries.size === syncableEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(syncableEntries.map(e => e.entry_id)));
    }
  };

  const toggleExpand = (entryId) => {
    setExpandedEntry(prev => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  const startEditing = (entry) => {
    setEditingEntry(entry);
    setEditedLines(JSON.parse(JSON.stringify(entry.lines || [])));
  };

  const cancelEditing = () => {
    setEditingEntry(null);
    setEditedLines([]);
  };

  const updateLine = (index, field, value) => {
    setEditedLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addLine = () => {
    setEditedLines(prev => [...prev, {
      line_num: prev.length + 1,
      account_code: '',
      account_name: '',
      purpose: '',
      debit: 0,
      credit: 0,
      memo: '',
    }]);
  };

  const removeLine = (index) => {
    setEditedLines(prev => prev.filter((_, i) => i !== index));
  };

  const saveEntry = async () => {
    if (!editingEntry) return;
    
    // Validate debits = credits
    const totalDebit = editedLines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
    const totalCredit = editedLines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Debits ($${totalDebit.toFixed(2)}) must equal Credits ($${totalCredit.toFixed(2)})`);
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/cointracker/journal-entries/${editingEntry.entry_id}`, {
        lines: editedLines.map((l, i) => ({
          ...l,
          line_num: i + 1,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry updated');
      setEditingEntry(null);
      setEditedLines([]);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'needs_review':
        return <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">Needs Review</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">Approved</span>;
      case 'synced':
        return <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">Synced</span>;
      case 'ignored':
        return <span className="px-2 py-1 text-xs rounded bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">Ignored</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded bg-zinc-500/20 text-zinc-400">{status}</span>;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'send': return 'text-red-400';
      case 'receive': return 'text-green-400';
      case 'swap': return 'text-blue-400';
      case 'sell': return 'text-orange-400';
      case 'transfer': return 'text-purple-400';
      case 'interest income': return 'text-yellow-400';
      default: return 'text-zinc-400';
    }
  };
  
  // Helper to get display type (prefer 'type' field, fallback to 'category')
  const getEntryType = (entry) => entry?.type || entry?.category || '';

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const approvedCount = entries.filter(e => e.status === 'approved').length;
  const needsReviewCount = entries.filter(e => e.status === 'needs_review').length;
  const syncedCount = entries.filter(e => e.status === 'synced' || e.xero_journal_id).length;
  const syncableEntries = entries.filter(e => e.status === 'approved' && !e.xero_journal_id);
  const selectedSyncable = [...selectedEntries].filter(id => syncableEntries.some(e => e.entry_id === id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Journal Entries</h2>
          <p className="text-sm text-zinc-400 mt-1">
            {pagination.total} entries • {needsReviewCount} need review • {approvedCount} approved • {syncedCount} synced
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="needs_review" className="text-white">Needs Review</SelectItem>
              <SelectItem value="approved" className="text-white">Approved</SelectItem>
              <SelectItem value="synced" className="text-white">Synced to Xero</SelectItem>
              <SelectItem value="ignored" className="text-white">Ignored</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={handleGenerateEntries}
            disabled={generating}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            Generate
          </Button>
          <Button
            onClick={handleRegenerateAll}
            disabled={generating}
            variant="outline"
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            title="Delete all entries and regenerate from scratch"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-2" />
            )}
            Regenerate All
          </Button>
          <Button
            onClick={handleSyncToXero}
            disabled={syncing || syncableEntries.length === 0 || !xeroConnected}
            className="bg-[#13B5EA] text-white hover:bg-[#0fa8db] disabled:opacity-50"
            title={!xeroConnected ? 'Connect Xero in Settings first' : selectedSyncable.length > 0 ? `Sync ${selectedSyncable.length} selected` : `Sync ${syncableEntries.length} approved`}
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {selectedSyncable.length > 0 ? `Sync (${selectedSyncable.length})` : 'Sync to Xero'}
          </Button>
        </div>
      </div>

      {/* Selection Bar - only show when there are syncable entries */}
      {syncableEntries.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
          >
            {selectedEntries.size === syncableEntries.length && syncableEntries.length > 0 ? (
              <CheckSquare className="w-4 h-4 text-[#13B5EA]" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            {selectedEntries.size === syncableEntries.length ? 'Deselect All' : 'Select All Approved'}
          </button>
          {selectedEntries.size > 0 && (
            <span className="text-sm text-zinc-400">
              {selectedSyncable.length} of {syncableEntries.length} ready to sync
            </span>
          )}
          {!xeroConnected && (
            <span className="text-sm text-amber-400 ml-auto">
              ⚠️ Connect Xero in Settings to sync entries
            </span>
          )}
        </div>
      )}

      {/* Entries List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <BookOpen className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No journal entries yet</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Click &quot;Generate&quot; to create journal entries from your transactions
              </p>
              <Button
                onClick={handleGenerateEntries}
                disabled={generating}
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Generate Entries
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const isExpanded = expandedEntry[entry.entry_id];
            const lines = entry.lines || [];
            const canSelect = entry.status === 'approved' && !entry.xero_journal_id;
            const isSelected = selectedEntries.has(entry.entry_id);
            
            return (
              <Card key={entry.entry_id} className={`bg-zinc-900/50 border-zinc-800 overflow-hidden ${isSelected ? 'ring-1 ring-[#13B5EA]/50' : ''}`}>
                <CardContent className="p-0">
                  {/* Header Row */}
                  <div 
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-zinc-800/30"
                    onClick={() => toggleExpand(entry.entry_id)}
                  >
                    {/* Checkbox for syncable entries */}
                    <div 
                      className="w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canSelect) toggleSelectEntry(entry.entry_id);
                      }}
                    >
                      {canSelect ? (
                        isSelected ? (
                          <CheckSquare className="w-5 h-5 text-[#13B5EA] cursor-pointer" />
                        ) : (
                          <Square className="w-5 h-5 text-zinc-500 cursor-pointer hover:text-zinc-300" />
                        )
                      ) : (
                        <div className="w-5 h-5" />
                      )}
                    </div>
                    
                    <div className="text-zinc-500">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    
                    <div className="w-16">
                      <span className="text-white font-mono">#{entry.entry_id?.slice(0, 6)}</span>
                    </div>
                    
                    <div className="w-28">
                      <p className="text-sm text-zinc-400">{formatDate(entry.date)}</p>
                    </div>
                    
                    <div className="w-20">
                      <span className={`text-sm font-medium capitalize ${getCategoryColor(getEntryType(entry))}`}>
                        {getEntryType(entry)}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">{entry.description}</p>
                      <p className="text-xs text-zinc-500">{entry.wallet_name}</p>
                    </div>
                    
                    <div className="w-24">
                      <div className="flex gap-1">
                        {entry.assets?.slice(0, 2).map((asset, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs rounded bg-zinc-700 text-zinc-300">
                            {asset}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="w-28 text-right">
                      <p className="text-white font-medium">{formatCurrency(entry.total_amount)}</p>
                    </div>
                    
                    <div className="w-32 flex items-center gap-2">
                      {getStatusBadge(entry.status)}
                      {entry.xero_journal_id && (
                        <span className="px-2 py-0.5 text-xs rounded bg-[#13B5EA]/20 text-[#13B5EA] border border-[#13B5EA]/30" title={`Xero ID: ${entry.xero_journal_id}`}>
                          Xero
                        </span>
                      )}
                    </div>
                    
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {entry.status === 'needs_review' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditing(entry)}
                            className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApproveEntry(entry.entry_id)}
                            className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Approve for Xero"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRegenerateEntry(entry.entry_id)}
                            className="h-8 w-8 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                            title="Regenerate"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleIgnoreEntry(entry.entry_id)}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-500/10"
                            title="Ignore"
                          >
                            <EyeOff className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {entry.status === 'approved' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateEntry(entry.entry_id)}
                          className="h-8 w-8 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                          title="Regenerate"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-zinc-800 bg-zinc-800/20">
                      {/* Transaction Summary Header */}
                      <div className="p-4 border-b border-zinc-800 bg-zinc-900/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div>
                              <p className="text-xs text-zinc-500 uppercase mb-1">Date</p>
                              <p className="text-white">{formatDate(entry.date)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500 uppercase mb-1">Type</p>
                              <p className={`font-medium capitalize ${getCategoryColor(getEntryType(entry))}`}>
                                {getEntryType(entry)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500 uppercase mb-1">Assets</p>
                              <div className="flex gap-1">
                                {entry.assets?.map((asset, idx) => (
                                  <span key={idx} className="px-2 py-0.5 text-xs rounded bg-zinc-700 text-white font-medium">
                                    {asset}
                                  </span>
                                ))}
                                {(!entry.assets || entry.assets.length === 0) && (
                                  <span className="text-zinc-500">-</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500 uppercase mb-1">Status</p>
                              {getStatusBadge(entry.status)}
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500 uppercase mb-1">Total</p>
                              <p className="text-white font-bold text-lg">{formatCurrency(entry.total_amount)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://solscan.io/tx/${entry.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 px-3 py-1.5 rounded border border-blue-500/30 hover:bg-blue-500/10"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View on Solscan
                            </a>
                          </div>
                        </div>
                      </div>
                      
                      {/* Journal Entry Lines */}
                      <div className="p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-zinc-400">Journal Entry Lines</h4>
                          {entry.status === 'needs_review' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(entry)}
                              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit Lines
                            </Button>
                          )}
                        </div>
                      
                        {/* Lines Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-zinc-700">
                                <th className="text-left p-2 text-xs text-zinc-500 uppercase">#</th>
                                <th className="text-left p-2 text-xs text-zinc-500 uppercase">Account</th>
                                <th className="text-left p-2 text-xs text-zinc-500 uppercase">Purpose</th>
                                <th className="text-right p-2 text-xs text-zinc-500 uppercase">Debit</th>
                                <th className="text-right p-2 text-xs text-zinc-500 uppercase">Credit</th>
                                <th className="text-left p-2 text-xs text-zinc-500 uppercase">Memo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lines.map((line, idx) => (
                                <tr key={idx} className="border-b border-zinc-800/50">
                                  <td className="p-2 text-sm text-zinc-500">{line.line_num || idx + 1}</td>
                                  <td className="p-2">
                                    <span className="text-sm font-mono text-zinc-500">#{line.account_code}</span>
                                    <span className="text-sm text-white ml-2">{getAccountName(line.account_code)}</span>
                                  </td>
                                  <td className="p-2 text-sm text-zinc-400">{line.purpose}</td>
                                  <td className="p-2 text-sm text-right">
                                    {line.debit > 0 ? (
                                      <span className="text-green-400">{formatCurrency(line.debit)}</span>
                                    ) : (
                                      <span className="text-zinc-600">-</span>
                                    )}
                                </td>
                                <td className="p-2 text-sm text-right">
                                  {line.credit > 0 ? (
                                    <span className="text-red-400">{formatCurrency(line.credit)}</span>
                                  ) : (
                                    <span className="text-zinc-600">-</span>
                                  )}
                                </td>
                                <td className="p-2 text-sm text-zinc-500">{line.memo || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-zinc-700">
                              <td colSpan="3" className="p-2 text-sm text-zinc-400 font-medium">Total</td>
                              <td className="p-2 text-sm text-right text-green-400 font-medium">
                                {formatCurrency(lines.reduce((sum, l) => sum + (l.debit || 0), 0))}
                              </td>
                              <td className="p-2 text-sm text-right text-red-400 font-medium">
                                {formatCurrency(lines.reduce((sum, l) => sum + (l.credit || 0), 0))}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      
                      {/* Applied Rules indicator */}
                      {entry.applied_rules && entry.applied_rules.length > 0 && (
                        <div className="mt-3 p-2 bg-purple-500/10 border border-purple-500/20 rounded">
                          <p className="text-xs text-purple-400">
                            Auto-categorized by rule: {entry.applied_rules.join(', ')}
                          </p>
                        </div>
                      )}
                      
                      {/* Reference Info */}
                      <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center gap-4 text-xs text-zinc-500">
                        <span>Reference: {entry.reference}</span>
                        <span>•</span>
                        <span>Hash: {entry.tx_hash?.slice(0, 20)}...</span>
                      </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-zinc-500">
                Page {pagination.page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  className="border-zinc-700"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  className="border-zinc-700"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => cancelEditing()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Journal Entry</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modify the journal entry lines before approving. Debits must equal Credits.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Entry Info */}
            <div className="flex gap-4 mb-4 text-sm">
              <span className="text-zinc-400">Date: <span className="text-white">{formatDate(editingEntry?.date)}</span></span>
              <span className="text-zinc-400">Type: <span className={getCategoryColor(getEntryType(editingEntry))}>{getEntryType(editingEntry)}</span></span>
              <span className="text-zinc-400">Wallet: <span className="text-white">{editingEntry?.wallet_name}</span></span>
            </div>

            {/* Editable Lines Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-2 text-xs text-zinc-500 uppercase w-10">#</th>
                    <th className="text-left p-2 text-xs text-zinc-500 uppercase" colSpan={2}>Account</th>
                    <th className="text-left p-2 text-xs text-zinc-500 uppercase">Purpose</th>
                    <th className="text-right p-2 text-xs text-zinc-500 uppercase w-28">Debit</th>
                    <th className="text-right p-2 text-xs text-zinc-500 uppercase w-28">Credit</th>
                    <th className="text-left p-2 text-xs text-zinc-500 uppercase">Memo</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {editedLines.map((line, idx) => (
                    <tr key={idx} className="border-b border-zinc-800/50">
                      <td className="p-2 text-sm text-zinc-500">{idx + 1}</td>
                      <td className="p-2" colSpan={2}>
                        <AccountSelectDropdown
                          value={line.account_code}
                          onChange={(code, name) => {
                            updateLine(idx, 'account_code', code);
                            updateLine(idx, 'account_name', name);
                          }}
                          accounts={accounts}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={line.purpose}
                          onChange={(e) => updateLine(idx, 'purpose', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white h-8"
                          placeholder="Purpose"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.debit || ''}
                          onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-green-400 h-8 text-right"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.credit || ''}
                          onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-red-400 h-8 text-right"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={line.memo || ''}
                          onChange={(e) => updateLine(idx, 'memo', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white h-8"
                          placeholder="Memo"
                        />
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLine(idx)}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-zinc-700">
                    <td colSpan="4" className="p-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addLine}
                        className="border-zinc-700"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Line
                      </Button>
                    </td>
                    <td className="p-2 text-right text-green-400 font-medium">
                      {formatCurrency(editedLines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0))}
                    </td>
                    <td className="p-2 text-right text-red-400 font-medium">
                      {formatCurrency(editedLines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0))}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Balance Check */}
            {(() => {
              const totalDebit = editedLines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
              const totalCredit = editedLines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
              const diff = Math.abs(totalDebit - totalCredit);
              if (diff > 0.01) {
                return (
                  <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded">
                    <p className="text-sm text-red-400">
                      ⚠️ Unbalanced: Difference of {formatCurrency(diff)}
                    </p>
                  </div>
                );
              }
              return (
                <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded">
                  <p className="text-sm text-green-400">
                    ✓ Balanced: Debits equal Credits
                  </p>
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelEditing}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEntry}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
